import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  RefreshCcw, Download, ChevronUp, ChevronDown, Search, Filter, RotateCcw, 
  Settings, Eye, EyeOff, GripVertical, Check, Clock, ExternalLink 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Combobox } from '../components/ui/combobox';
import { ExportDialog } from '../components/ExportDialog';
import { API } from '@/lib/apiBase';

// Format currency
const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency
  }).format(amount || 0);
};

// Format amount with conversion
const formatAmountWithConversion = (originalAmount, eurAmount, currency = 'EUR') => {
  if (!currency || currency === 'EUR') {
    return formatCurrency(originalAmount, 'EUR');
  }
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span>{formatCurrency(originalAmount, currency)}</span>
      <span className="text-xs text-muted-foreground">({formatCurrency(eurAmount, 'EUR')})</span>
    </span>
  );
};

// Sortable header component
const SortableHeader = ({ column, sortColumn, sortDirection, onSort, children, className = '' }) => (
  <TableHead 
    onClick={() => onSort(column)}
    className={`cursor-pointer hover:bg-muted/50 select-none ${className}`}
  >
    <div className="flex items-center gap-1">
      {children}
      {sortColumn === column && (
        sortDirection === 'asc' 
          ? <ChevronUp className="w-4 h-4" />
          : <ChevronDown className="w-4 h-4" />
      )}
    </div>
  </TableHead>
);

// Column configuration for settings
const ALL_COLUMNS = [
  { key: 'invoice_number', label: 'N° Facture', required: true },
  { key: 'invoice_date', label: 'Date facture' },
  { key: 'product_name', label: 'Produit' },
  { key: 'category_name', label: 'Catégorie' },
  { key: 'supplier_name', label: 'Fournisseur' },
  { key: 'payer_company_name', label: 'Payeur' },
  { key: 'beneficiary_company_name', label: 'Bénéficiaire' },
  { key: 'establishment_name', label: 'Établissement' },
  { key: 'sector_name', label: 'Secteur' },
  { key: 'user_name', label: 'Utilisateur' },
  { key: 'amount_ht', label: 'Montant HT', align: 'right' },
  { key: 'amount_ttc', label: 'Montant TTC', align: 'right' },
  { key: 'refacturation_status', label: 'Statut' },
];

const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.map(c => c.key);
const DEFAULT_COLUMN_ORDER = ALL_COLUMNS.map(c => c.key);

export const Refacturation = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [allLines, setAllLines] = useState([]);
  
  // Reference data
  const [companies, setCompanies] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [payerFilter, setPayerFilter] = useState('');
  const [beneficiaryFilter, setBeneficiaryFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Sorting
  const [sortColumn, setSortColumn] = useState('invoice_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Column settings
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMN_ORDER);
  const [draggedColumn, setDraggedColumn] = useState(null);
  
  // Export
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  // Status update
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Fetch companies
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/external/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  }, [token]);

  // Fetch all refacturation lines
  const fetchRefacturationLines = useCallback(async () => {
    setLoading(true);
    try {
      // Get all entry lines that have refacturation data
      const response = await axios.get(`${API}/entry-lines/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter only lines that are "refacturable" (payer != beneficiary)
      const refacturableLines = response.data.filter(line => 
        line.payer_company_name && 
        line.company_name && 
        line.payer_company_name !== line.company_name
      );
      
      setAllLines(refacturableLines);
    } catch (error) {
      console.error('Error fetching refacturation lines:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Load column preferences
  const loadColumnPreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/preferences/refacturation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const prefs = response.data;
      
      if (prefs.visible_columns && prefs.visible_columns.length > 0) {
        const allColumnKeys = ALL_COLUMNS.map(c => c.key);
        const savedVisible = prefs.visible_columns.filter(k => allColumnKeys.includes(k));
        setVisibleColumns(savedVisible);
      }
      if (prefs.column_order && prefs.column_order.length > 0) {
        const allColumnKeys = ALL_COLUMNS.map(c => c.key);
        const savedOrder = prefs.column_order.filter(k => allColumnKeys.includes(k));
        const newColumns = allColumnKeys.filter(k => !prefs.column_order.includes(k));
        setColumnOrder([...savedOrder, ...newColumns]);
      }
    } catch (error) {
      console.error('Error loading column preferences:', error);
    }
  }, [token]);

  // Save column preferences
  const saveColumnPreferences = useCallback(async (visible, order) => {
    try {
      await axios.put(`${API}/user/preferences/refacturation`, {
        visible_columns: visible,
        column_order: order
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving column preferences:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchCompanies();
    fetchRefacturationLines();
    loadColumnPreferences();
  }, [fetchCompanies, fetchRefacturationLines, loadColumnPreferences]);

  useEffect(() => {
    const status = searchParams.get('status');
    const dateFrom = searchParams.get('date_from');
    const dateTo = searchParams.get('date_to');

    if (status === 'pending' || status === 'refactured') {
      setStatusFilter(status);
      setShowFilters(true);
    }
    if (dateFrom || dateTo) {
      if (dateFrom) setStartDate(dateFrom);
      if (dateTo) setEndDate(dateTo);
      setShowFilters(true);
    }
  }, [searchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, payerFilter, beneficiaryFilter, supplierFilter, productFilter, categoryFilter, statusFilter, startDate, endDate]);

  // Company options for filters
  const companyOptions = useMemo(() => {
    return companies.map(c => ({ value: c.name, label: c.name }));
  }, [companies]);

  // Supplier options
  const supplierOptions = useMemo(() => {
    const suppliers = [...new Set(allLines.map(l => l.supplier_name).filter(Boolean))];
    return suppliers.sort().map(s => ({ value: s, label: s }));
  }, [allLines]);

  // Product options - filtered by category if selected
  const productOptions = useMemo(() => {
    let filteredLines = allLines;
    if (categoryFilter) {
      filteredLines = allLines.filter(l => l.category_name === categoryFilter);
    }
    const products = [...new Set(filteredLines.map(l => l.product_name).filter(Boolean))];
    return products.sort().map(p => ({ value: p, label: p }));
  }, [allLines, categoryFilter]);

  // Category options
  const categoryOptions = useMemo(() => {
    const categories = [...new Set(allLines.map(l => l.category_name).filter(Boolean))];
    return categories.sort().map(c => ({ value: c, label: c }));
  }, [allLines]);

  // Reset product filter when category changes
  useEffect(() => {
    if (categoryFilter) {
      // Check if the current product filter is still valid for the new category
      const validProducts = allLines
        .filter(l => l.category_name === categoryFilter)
        .map(l => l.product_name);
      if (productFilter && !validProducts.includes(productFilter)) {
        setProductFilter('');
      }
    }
  }, [categoryFilter, allLines, productFilter]);

  // Filter and sort data
  const filteredAndSortedLines = useMemo(() => {
    let result = [...allLines];
    
    // Text search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(line =>
        line.invoice_number?.toLowerCase().includes(search) ||
        line.product_name?.toLowerCase().includes(search) ||
        line.category_name?.toLowerCase().includes(search) ||
        line.supplier_name?.toLowerCase().includes(search) ||
        line.payer_company_name?.toLowerCase().includes(search) ||
        line.company_name?.toLowerCase().includes(search) ||
        line.establishment_name?.toLowerCase().includes(search) ||
        line.sector_name?.toLowerCase().includes(search) ||
        line.user_name?.toLowerCase().includes(search)
      );
    }
    
    // Apply filters
    if (payerFilter) result = result.filter(l => l.payer_company_name === payerFilter);
    if (beneficiaryFilter) result = result.filter(l => l.company_name === beneficiaryFilter);
    if (supplierFilter) result = result.filter(l => l.supplier_name === supplierFilter);
    if (productFilter) result = result.filter(l => l.product_name === productFilter);
    if (categoryFilter) result = result.filter(l => l.category_name === categoryFilter);
    if (statusFilter && statusFilter !== 'all') result = result.filter(l => l.refacturation_status === statusFilter);
    if (startDate) result = result.filter(l => l.invoice_date >= startDate);
    if (endDate) result = result.filter(l => l.invoice_date <= endDate);
    
    // Sorting
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'invoice_number': aVal = a.invoice_number || ''; bVal = b.invoice_number || ''; break;
        case 'invoice_date': aVal = a.invoice_date || ''; bVal = b.invoice_date || ''; break;
        case 'product_name': aVal = a.product_name || ''; bVal = b.product_name || ''; break;
        case 'category_name': aVal = a.category_name || ''; bVal = b.category_name || ''; break;
        case 'supplier_name': aVal = a.supplier_name || ''; bVal = b.supplier_name || ''; break;
        case 'payer_company_name': aVal = a.payer_company_name || ''; bVal = b.payer_company_name || ''; break;
        case 'beneficiary_company_name': aVal = a.company_name || ''; bVal = b.company_name || ''; break;
        case 'establishment_name': aVal = a.establishment_name || ''; bVal = b.establishment_name || ''; break;
        case 'sector_name': aVal = a.sector_name || ''; bVal = b.sector_name || ''; break;
        case 'user_name': aVal = a.user_name || ''; bVal = b.user_name || ''; break;
        case 'amount_ht': aVal = a.amount_ht || 0; bVal = b.amount_ht || 0; break;
        case 'amount_ttc': aVal = a.amount_ttc || 0; bVal = b.amount_ttc || 0; break;
        case 'refacturation_status': aVal = a.refacturation_status || ''; bVal = b.refacturation_status || ''; break;
        default: aVal = a.invoice_date || ''; bVal = b.invoice_date || '';
      }
      if (typeof aVal === 'string') {
        const compare = aVal.localeCompare(bVal, 'fr');
        return sortDirection === 'asc' ? compare : -compare;
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [allLines, searchTerm, payerFilter, beneficiaryFilter, supplierFilter, productFilter, categoryFilter, statusFilter, startDate, endDate, sortColumn, sortDirection]);

  // Totals
  const totals = useMemo(() => {
    return filteredAndSortedLines.reduce((acc, line) => ({
      total_ht: acc.total_ht + (line.amount_ht || 0),
      total_ttc: acc.total_ttc + (line.amount_ttc || 0)
    }), { total_ht: 0, total_ttc: 0 });
  }, [filteredAndSortedLines]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedLines.length / itemsPerPage);
  const paginatedLines = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedLines.slice(start, start + itemsPerPage);
  }, [filteredAndSortedLines, currentPage, itemsPerPage]);

  // Check if filters are active
  const hasActiveFilters = payerFilter || beneficiaryFilter || supplierFilter || productFilter || categoryFilter || (statusFilter && statusFilter !== 'all') || startDate || endDate || searchTerm;

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setPayerFilter('');
    setBeneficiaryFilter('');
    setSupplierFilter('');
    setProductFilter('');
    setCategoryFilter('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Selection handlers
  const isAllSelected = paginatedLines.length > 0 && paginatedLines.every(line => selectedIds.has(line.id));
  const isSomeSelected = paginatedLines.some(line => selectedIds.has(line.id)) && !isAllSelected;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedLines.map(line => line.id)));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Column settings handlers
  const toggleColumn = (columnKey) => {
    const column = ALL_COLUMNS.find(c => c.key === columnKey);
    if (column?.required) return;
    
    setVisibleColumns(prev => 
      prev.includes(columnKey)
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === columnKey) return;
    
    setColumnOrder(prev => {
      const newOrder = [...prev];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(columnKey);
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedColumn);
      return newOrder;
    });
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const handleSaveSettings = async () => {
    await saveColumnPreferences(visibleColumns, columnOrder);
    setSettingsDialogOpen(false);
    toast.success('Préférences sauvegardées');
  };

  const handleResetSettings = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setColumnOrder(DEFAULT_COLUMN_ORDER);
  };

  // Get ordered and visible columns for display
  const displayColumns = useMemo(() => {
    return columnOrder
      .filter(key => visibleColumns.includes(key))
      .map(key => ALL_COLUMNS.find(c => c.key === key))
      .filter(Boolean);
  }, [columnOrder, visibleColumns]);

  // Update refacturation status
  const updateStatus = async (newStatus) => {
    if (selectedIds.size === 0) return;
    
    setUpdatingStatus(true);
    try {
      await axios.post(`${API}/refacturation/status`, {
        line_ids: Array.from(selectedIds),
        is_refactured: newStatus === 'refactured'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Statut mis à jour pour ${selectedIds.size} ligne(s)`);
      setSelectedIds(new Set());
      fetchRefacturationLines();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Get cell value for a column
  const getCellValue = (line, columnKey) => {
    switch (columnKey) {
      case 'invoice_number':
        return <span className="font-medium">{line.invoice_number}</span>;
      case 'invoice_date':
        return (
          <span className="text-sm">
            {line.invoice_date ? new Date(line.invoice_date).toLocaleDateString('fr-FR') : '-'}
          </span>
        );
      case 'product_name':
        return <span className="text-sm">{line.product_name}</span>;
      case 'category_name':
        return <span className="text-sm">{line.category_name || '-'}</span>;
      case 'supplier_name':
        return <span className="text-sm">{line.supplier_name || '-'}</span>;
      case 'payer_company_name':
        return <span className="text-sm">{line.payer_company_name}</span>;
      case 'beneficiary_company_name':
        return <span className="text-sm">{line.company_name}</span>;
      case 'establishment_name':
        return <span className="text-sm">{line.establishment_name || '-'}</span>;
      case 'sector_name':
        return <span className="text-sm">{line.sector_name || '-'}</span>;
      case 'user_name':
        return <span className="text-sm">{line.user_name || '-'}</span>;
      case 'amount_ht':
        return formatAmountWithConversion(line.amount_ht, line.amount_ht_eur, line.currency);
      case 'amount_ttc':
        return <span className="font-medium">{formatAmountWithConversion(line.amount_ttc, line.amount_ttc_eur, line.currency)}</span>;
      case 'refacturation_status':
        return line.refacturation_status === 'refactured' ? (
          <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">
            <Check className="w-3 h-3 mr-1" />
            Refacturé
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500/30">
            <Clock className="w-3 h-3 mr-1" />
            En attente
          </Badge>
        );
      default:
        return line[columnKey] || '-';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Refacturation</h1>
          <p className="text-muted-foreground">
            <span className="text-primary font-medium">
              {filteredAndSortedLines.length} ligne{filteredAndSortedLines.length > 1 ? 's' : ''}
            </span>
            {' '}à refacturer
            {selectedIds.size > 0 && (
              <span className="text-orange-500"> • {selectedIds.size} sélectionnée{selectedIds.size > 1 ? 's' : ''}</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <>
              <Button 
                variant="outline" 
                onClick={() => updateStatus('refactured')}
                disabled={updatingStatus}
                className="text-green-500 border-green-500/30 hover:bg-green-500/10"
              >
                <Check className="w-4 h-4 mr-2" />
                Marquer refacturé ({selectedIds.size})
              </Button>
              <Button 
                variant="outline" 
                onClick={() => updateStatus('pending')}
                disabled={updatingStatus}
                className="text-orange-500 border-orange-500/30 hover:bg-orange-500/10"
              >
                <Clock className="w-4 h-4 mr-2" />
                Marquer en attente ({selectedIds.size})
              </Button>
            </>
          )}
          <Button variant="outline" onClick={fetchRefacturationLines} disabled={loading}>
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Search + Filters + Actions */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative min-w-[250px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-refacturation"
            />
          </div>

          {/* Filters toggle */}
          <Button 
            variant={showFilters ? "default" : "outline"} 
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtres avancés
            {hasActiveFilters && <span className="ml-2 w-2 h-2 bg-red-500 rounded-full" />}
          </Button>

          {hasActiveFilters && (
            <Button variant="ghost" onClick={resetFilters} data-testid="reset-filters-btn">
              <RotateCcw className="w-4 h-4 mr-2" />
              Réinitialiser
            </Button>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setSettingsDialogOpen(true)}
            data-testid="settings-refacturation-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
          <Button variant="outline" onClick={() => setExportDialogOpen(true)}>
            <Download className="w-4 h-4 mr-2" />
            Exporter {selectedIds.size > 0 ? `(${selectedIds.size})` : hasActiveFilters ? `(${filteredAndSortedLines.length})` : ''}
          </Button>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Société payeuse</Label>
              <Combobox
                options={companyOptions}
                value={payerFilter}
                onValueChange={setPayerFilter}
                placeholder="Toutes"
                emptyText="Aucune société"
                data-testid="filter-payer"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Société à refacturer</Label>
              <Combobox
                options={companyOptions}
                value={beneficiaryFilter}
                onValueChange={setBeneficiaryFilter}
                placeholder="Toutes"
                emptyText="Aucune société"
                data-testid="filter-beneficiary"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Catégorie</Label>
              <Combobox
                options={categoryOptions}
                value={categoryFilter}
                onValueChange={setCategoryFilter}
                placeholder="Toutes"
                emptyText="Aucune catégorie"
                data-testid="filter-category"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Produit</Label>
              <Combobox
                options={productOptions}
                value={productFilter}
                onValueChange={setProductFilter}
                placeholder="Tous"
                emptyText="Aucun produit"
                data-testid="filter-product"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Fournisseur</Label>
              <Combobox
                options={supplierOptions}
                value={supplierFilter}
                onValueChange={setSupplierFilter}
                placeholder="Tous"
                emptyText="Aucun fournisseur"
                data-testid="filter-supplier"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Statut</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="refactured">Refacturé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date début</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date fin</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Nombre de lignes</p>
          <p className="text-2xl font-bold">{filteredAndSortedLines.length}</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Total HT</p>
          <p className="text-2xl font-bold">{formatCurrency(totals.total_ht)}</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Total TTC</p>
          <p className="text-2xl font-bold text-primary">{formatCurrency(totals.total_ttc)}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox 
                  checked={isAllSelected}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Tout sélectionner"
                  className={isSomeSelected ? "opacity-50" : ""}
                  data-testid="select-all-checkbox"
                />
              </TableHead>
              {displayColumns.map((column) => (
                <SortableHeader 
                  key={column.key}
                  column={column.key} 
                  sortColumn={sortColumn} 
                  sortDirection={sortDirection} 
                  onSort={handleSort}
                  className={column.align === 'right' ? 'text-right' : ''}
                >
                  {column.label}
                </SortableHeader>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length + 1} className="text-center py-8 text-muted-foreground">
                  <RefreshCcw className="w-8 h-8 mx-auto mb-2 animate-spin opacity-50" />
                  <p>Chargement...</p>
                </TableCell>
              </TableRow>
            ) : paginatedLines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length + 1} className="text-center py-8 text-muted-foreground">
                  <RefreshCcw className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune ligne à refacturer trouvée</p>
                  {hasActiveFilters && (
                    <Button variant="link" onClick={resetFilters} className="mt-2">
                      Réinitialiser les filtres
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedLines.map((line) => (
                <TableRow 
                  key={line.id}
                  className={selectedIds.has(line.id) ? "bg-primary/5" : ""}
                >
                  <TableCell>
                    <Checkbox 
                      checked={selectedIds.has(line.id)}
                      onCheckedChange={() => toggleSelectOne(line.id)}
                      aria-label={`Sélectionner ${line.invoice_number}`}
                    />
                  </TableCell>
                  {displayColumns.map((column) => (
                    <TableCell 
                      key={column.key}
                      className={column.align === 'right' ? 'text-right' : ''}
                    >
                      {getCellValue(line, column.key)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Lignes par page:</Label>
            <Select value={String(itemsPerPage)} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
              <SelectTrigger className="w-[70px] h-8" data-testid="items-per-page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} sur {totalPages}
            </span>
            <div className="flex gap-1">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                ««
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                «
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                »
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                »»
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        dataType="refacturation"
        filters={{
          line_ids: selectedIds.size > 0 
            ? Array.from(selectedIds) 
            : filteredAndSortedLines.map(l => l.id)
        }}
        title={selectedIds.size > 0 
          ? `Exporter ${selectedIds.size} ligne(s) sélectionnée(s)`
          : hasActiveFilters 
            ? `Exporter ${filteredAndSortedLines.length} ligne(s) filtrée(s)`
            : "Exporter les lignes de refacturation"
        }
      />

      {/* Settings Dialog for Column Preferences */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="bg-card max-w-md" data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle>Paramètres du tableau</DialogTitle>
            <DialogDescription>
              Sélectionnez les colonnes à afficher et réorganisez-les par glisser-déposer
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label className="text-sm font-medium mb-3 block">Colonnes visibles</Label>
            <div className="space-y-1 border rounded-lg p-2 bg-muted/30">
              {columnOrder.map((columnKey) => {
                const column = ALL_COLUMNS.find(c => c.key === columnKey);
                if (!column) return null;
                const isVisible = visibleColumns.includes(columnKey);
                const isDragging = draggedColumn === columnKey;
                
                return (
                  <div
                    key={columnKey}
                    draggable
                    onDragStart={(e) => handleDragStart(e, columnKey)}
                    onDragOver={(e) => handleDragOver(e, columnKey)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center gap-3 p-2 rounded-md cursor-move
                      ${isDragging ? 'bg-primary/20 opacity-50' : 'hover:bg-muted'}
                      ${!isVisible ? 'opacity-50' : ''}
                      transition-all duration-150
                    `}
                    data-testid={`column-item-${columnKey}`}
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <Checkbox
                      id={`col-${columnKey}`}
                      checked={isVisible}
                      onCheckedChange={() => toggleColumn(columnKey)}
                      disabled={column.required}
                      data-testid={`column-checkbox-${columnKey}`}
                    />
                    <label 
                      htmlFor={`col-${columnKey}`}
                      className="flex-1 text-sm cursor-pointer select-none"
                    >
                      {column.label}
                      {column.required && (
                        <span className="text-xs text-muted-foreground ml-2">(obligatoire)</span>
                      )}
                    </label>
                    {isVisible ? (
                      <Eye className="w-4 h-4 text-green-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Glissez les éléments pour réorganiser les colonnes
            </p>
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="ghost" onClick={handleResetSettings} data-testid="reset-settings-btn">
              Réinitialiser
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveSettings} data-testid="save-settings-btn">
                Sauvegarder
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Refacturation;
